import { standardSchemaResolver } from '@hookform/resolvers/standard-schema';
import { useState } from 'react';
import { useForm, type SubmitHandler } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import type { Organization } from '@/types/gql.generated';
import {
  useToast,
  Button,
  Flex,
  FormControl,
  FormErrorMessage,
  Icon,
  IconButton,
  Input,
  Text,
} from '@/ui';
import { getGQLErrorMessage, isGQLError } from '@/utils/errors';
import {
  OrganizationNameSchema,
  type OrganizationNameDto,
} from '../dto/organizationName.dto';
import { useUpdateOrganization } from '../hooks/useUpdateOrganization';

type Props = {
  organization: Organization;
};

export const EditableOrganizationName = ({ organization }: Props) => {
  const { t } = useTranslation('settingsModal', {
    keyPrefix: 'organizations',
  });
  const [isEditing, setIsEditing] = useState(false);
  const { updateName, isPending } = useUpdateOrganization(organization.id);
  const toast = useToast();

  const {
    register,
    handleSubmit: onSubmit,
    reset,
    setError,
    formState: { errors },
  } = useForm({
    defaultValues: { name: organization.name },
    resolver: standardSchemaResolver(OrganizationNameSchema),
  });

  const startEditing = () => {
    reset({ name: organization.name });
    setIsEditing(true);
  };

  const cancelEditing = () => {
    reset({ name: organization.name });
    setIsEditing(false);
  };

  const handleSave: SubmitHandler<OrganizationNameDto> = ({ name }) => {
    if (name === organization.name) {
      setIsEditing(false);
      return;
    }

    updateName(name, {
      onError: (err) => {
        setError('name', {
          message: isGQLError(err)
            ? (getGQLErrorMessage(err) ?? t('name.error'))
            : t('name.error'),
        });
      },
      onSuccess: () => {
        setIsEditing(false);
        toast.notify(t('name.success'));
      },
    });
  };

  if (!isEditing) {
    return (
      <Flex align="center" gap="1" minW="0">
        <Text noOfLines={1}>{organization.name}</Text>
        <IconButton
          aria-label={t('name.edit')}
          colorScheme="dark"
          icon={<Icon icon="Pencil" size="4" />}
          size="xs"
          variant="ghost"
          onClick={startEditing}
        />
      </Flex>
    );
  }

  return (
    <Flex
      align="flex-start"
      as="form"
      flex="1"
      gap="2"
      minW="0"
      onSubmit={onSubmit(handleSave)}
    >
      {/* Workspace names run long, and the header has the room — let the field
          take whatever the Save/Cancel buttons don't need. */}
      <FormControl flex="1" isInvalid={!!errors.name} minW="0">
        <Input autoFocus size="sm" {...register('name')} />
        <FormErrorMessage>{errors.name?.message}</FormErrorMessage>
      </FormControl>
      <Button flexShrink="0" isLoading={isPending} size="sm" type="submit">
        {t('name.save')}
      </Button>
      <Button
        flexShrink="0"
        isDisabled={isPending}
        size="sm"
        variant="secondary"
        onClick={cancelEditing}
      >
        {t('name.cancel')}
      </Button>
    </Flex>
  );
};
